/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Accordion, AccordionPanel, Button, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { AccordionProps } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import AccordionHeader from 'bundles/unified-description-page-common/components/accordions/HeaderButtonRedesign';
import type { CourseItemContentElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemContent';
import type { CourseItemSubtitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemSubtitle';
import type { SyllabusItemTitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import SyllabusItemTitle from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import Modal, { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  accordion: (hasDivider: boolean) => css`
    position: relative;
    padding: 0;
    margin: 0;
    ${hasDivider ? `margin-top: -8px;` : ''}

    ${hasDivider ? `border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);` : 'border-top: none;'}

    &::before {
      content: '';
      position: absolute;
      top: 0;
      height: 2px;
      left: var(--cds-spacing-200);
      right: var(--cds-spacing-200);
    }
  `,
  panel: css`
    margin: 0;
    padding: 0 var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
  `,
  button: css`
    justify-content: space-between;
    text-align: left;
    border-radius: 0;

    /* hide the focus ring that overlaps adjacent buttons */
    overflow: hidden;
  `,
  mobileSyllabusItem: css`
    & + & {
      ${xdpStyles.border('border-top')}
    }

    &:hover h3 {
      text-decoration: underline;
    }
  `,
  headerContent: css`
    display: flex;
    gap: var(--cds-spacing-200);
    min-width: 0;
  `,
  titleContent: css`
    min-width: 0;
  `,

  subTitleContainer: css`
    display: flex;
    margin-top: var(--cds-spacing-50);
    white-space: nowrap;

    ${breakpoints.down('sm')} {
      flex-wrap: wrap;
      white-space: unset;
    }
  `,
};

interface Props {
  children: [SyllabusItemTitleElement, CourseItemSubtitleElement, CourseItemContentElement];
  itemIndex: number;
  thumbnail?: React.ReactNode;
  timeDuration: number;
}

function SyllabusItemRedesignV2({
  children,
  itemIndex,
  thumbnail,
  timeDuration,
}: Props): React.ReactElement<AccordionProps | 'div'> {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { isModalOpen, setModalOpen } = useModal();

  const [title, , panel] = React.Children.toArray(children) as [
    SyllabusItemTitleElement,
    CourseItemSubtitleElement,
    CourseItemContentElement
  ];

  if (isMobile) {
    return (
      <div css={styles.mobileSyllabusItem} className="syllabus-item-redesign-v2">
        <Button
          variant="ghost"
          icon={<ChevronNextIcon size="medium" />}
          css={styles.button}
          fullWidth
          onClick={setModalOpen}
        >
          <div css={styles.headerContent}>
            <div>
              <SyllabusItemTitle {...title.props} disableLink={true} />
              <Typography2 variant="bodySecondary" color="supportText" component="div" css={styles.subTitleContainer}>
                <FormattedMessage
                  message={_t('{timeDuration, plural, =1 {# hour} other {# hours}}')}
                  timeDuration={timeDuration}
                />
              </Typography2>
            </div>
          </div>
        </Button>
        <Modal open={isModalOpen} setOpen={setModalOpen} header={title} hideActions>
          {panel}
        </Modal>
      </div>
    );
  }

  return (
    <Accordion
      variant="silent"
      css={styles.accordion(itemIndex > 1)}
      className="syllabus-item-redesign-v2"
      defaultExpanded={itemIndex === 1}
    >
      <AccordionHeader label={_t('Skill goal details')}>
        <div css={styles.headerContent}>
          <div css={styles.titleContent}>
            {title}
            <Typography2 variant="bodySecondary" color="supportText" component="div" css={styles.subTitleContainer}>
              <FormattedMessage
                message={_t('{timeDuration, plural, =1 {# hour} other {# hours}}')}
                timeDuration={timeDuration}
              />
            </Typography2>
          </div>
        </div>
      </AccordionHeader>
      <AccordionPanel css={styles.panel}>{panel}</AccordionPanel>
    </Accordion>
  );
}

export default SyllabusItemRedesignV2;
